// src/lib/posthog/client.ts
//
// The only module in the app that talks to posthog-js. Every export returns
// void and never throws: an SDK or browser-storage failure must be
// invisible to merchants, so every call is wrapped and routed to
// `logClientError` (name only, never a message).
//
// Safe to import from the server: each helper returns early when `window` is
// undefined, so nothing here runs during SSR. The consent decision itself
// lives in `src/lib/consent.ts`; this module only executes it.

import posthog from "posthog-js";
import type { BeforeSendFn } from "posthog-js";

import { classifyTrafficSource, referrerOrigin } from "./attribution";
import { logClientError } from "./client-log";
import { getPosthogClientEnv } from "./config";
import type { EventName } from "./events";
import { buildInitOptions } from "./init-options";
import { pickAllowed } from "./properties";
import { preloadRemoteConfig } from "./remote-config";
import { replayAllowed, shouldDropEvent, surfaceConfig } from "./replay-policy";
import { redactEvent, redactUrl } from "./sanitize";

/** Every localStorage key this app writes is namespaced, so a rejection
 *  can drop them all without touching PostHog's own `__ph_opt_in_out_*` key. */
const STORAGE_PREFIX = "niftipay.ph.";
const FIRST_TOUCH_KEY = `${STORAGE_PREFIX}first_touch.v1`;
/** posthog-js writes `ph_<key>_posthog`; its opt-out key starts with `__ph_`. */
const COOKIE_PREFIX = "ph_";

/** The first touch as stored, registered and read back. */
type FirstTouch = {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  referrer: string | null;
  landing_page: string | null;
  traffic_source: string | null;
};

const FIRST_TOUCH_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "referrer",
  "landing_page",
  "traffic_source",
] as const satisfies readonly (keyof FirstTouch)[];

// The `$set_once` person-property name of each stored key.
const PERSON_PROPERTY_BY_KEY: Record<string, string> = {
  utm_source: "first_touch_utm_source",
  utm_medium: "first_touch_utm_medium",
  utm_campaign: "first_touch_utm_campaign",
  utm_content: "first_touch_utm_content",
  utm_term: "first_touch_utm_term",
  referrer: "first_touch_referrer",
  landing_page: "first_touch_landing_page",
  traffic_source: "first_touch_source",
};

/** The only capture option any caller may set. */
export interface CaptureOptions {
  transport?: "sendBeacon";
}

// Module-level "once" flags: one init and one disabled line per process.
let initialised = false;
let disabledLogged = false;

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function hasKey(): boolean {
  return getPosthogClientEnv().key !== "";
}

function isLoaded(): boolean {
  try {
    return posthog.__loaded === true;
  } catch {
    return false;
  }
}

/** Browser + key + loaded SDK + not opted out. */
function isActive(): boolean {
  if (!isBrowser() || !hasKey() || !isLoaded()) return false;
  try {
    return !posthog.has_opted_out_capturing();
  } catch {
    return false;
  }
}

function errorName(error: unknown): string {
  return error instanceof Error ? error.name : "UnknownError";
}

/** Runs an SDK call so a failure is logged as a shape and never propagates. */
function run(scope: string, action: () => void): void {
  try {
    action();
  } catch (error) {
    logClientError(scope, { name: errorName(error) });
  }
}

function readStoredFirstTouch(): Record<string, unknown> | null {
  const raw = window.localStorage.getItem(FIRST_TOUCH_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    // Malformed storage is treated as no first touch, so the visit is
    // resolved again instead of registering garbage as super properties.
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    // Only our own shape is trusted: anything without a `traffic_source` is
    // not a first touch, and unknown keys never become super properties.
    if (typeof record.traffic_source !== "string") return null;
    const stored: Record<string, unknown> = {};
    for (const key of FIRST_TOUCH_KEYS) stored[key] = record[key] ?? null;
    return stored;
  } catch {
    return null;
  }
}

function resolveFirstTouch(): FirstTouch {
  const params = new URLSearchParams(window.location.search);
  const param = (key: string): string | null => params.get(key) || null;
  // Only the referrer's origin is stored, never the
  // full URL. The raw value is still what gets classified: an
  // `android-app://` referrer (the Gmail app) has no http(s) origin but does
  // have a host, and `traffic_source` carries only that host.
  const rawReferrer = document.referrer || null;
  const referrer = referrerOrigin(rawReferrer);
  const utmSource = param("utm_source");
  const utmMedium = param("utm_medium");
  return {
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: param("utm_campaign"),
    utm_content: param("utm_content"),
    utm_term: param("utm_term"),
    referrer,
    // Path only, redacted: a landing page can be `/paylink/<id>`.
    landing_page: redactUrl(window.location.pathname),
    traffic_source: classifyTrafficSource({
      utmSource,
      utmMedium,
      gclid: param("gclid"),
      referrer: rawReferrer,
      ownHost: window.location.hostname,
    }),
  };
}

function firstTouchPersonProperties(
  firstTouch: Record<string, unknown>,
): Record<string, unknown> {
  const properties: Record<string, unknown> = {};
  for (const [key, personKey] of Object.entries(PERSON_PROPERTY_BY_KEY)) {
    properties[personKey] = firstTouch[key] ?? null;
  }
  return properties;
}

function registerFirstTouch(): void {
  const stored = readStoredFirstTouch();
  if (stored !== null) {
    // A returning visitor: the stored values are registered again for this
    // session, never overwritten and never `$set_once` a second time.
    posthog.register(stored);
    return;
  }
  const firstTouch = resolveFirstTouch();
  window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(firstTouch));
  posthog.register(firstTouch);
  posthog.setPersonProperties(undefined, firstTouchPersonProperties(firstTouch));
}

/**
 * Drops a replay `$snapshot` or a `$dead_click` captured while the current
 * pathname is blocked, before it is redacted or sent. Every other event passes through
 * unchanged; `null` (an already-dropped event) stays `null`.
 */
const dropBlockedSnapshots: BeforeSendFn = (cr) => {
  if (cr === null) return null;
  // Read at flush time: a snapshot buffered on an allowed page and flushed
  // after entering a blocked one is dropped too (over-drop is the safe side);
  // the inverse cannot happen because the layout effect stops recording
  // before rrweb observes a blocked page.
  return shouldDropEvent(cr.event, window.location.pathname) ? null : cr;
};

/**
 * Initialises posthog-js once per process and resolves the first touch. With
 * an empty key it writes exactly one console line and returns: the product
 * works, nothing is captured.
 */
export function startPostHog(): void {
  if (!isBrowser()) return;
  const env = getPosthogClientEnv();
  if (env.key === "") {
    if (!disabledLogged) {
      disabledLogged = true;
      console.info("[posthog] capture disabled: NEXT_PUBLIC_POSTHOG_KEY is empty");
    }
    return;
  }
  if (initialised) return;
  initialised = true;
  run("start", () => {
    // The config must be applied before init, or replay never starts
    // once `/flags/` (init-options.ts) is off (remote-config.ts).
    preloadRemoteConfig(env.key);
    posthog.init(
      env.key,
      buildInitOptions({
        apiHost: env.ingestHost,
        uiHost: env.uiHost,
        beforeSend: (cr) => redactEvent(dropBlockedSnapshots(cr)),
      }),
    );
    registerFirstTouch();
  });
}

// `ph_*` on the exact host, on the registrable domain and with no
// domain at all — the same three variants as `src/lib/google-analytics.ts`,
// because posthog-js writes to the registrable domain when it can.
function clearPosthogCookies(): void {
  const host = window.location.hostname;
  const parts = host.split(".");
  const domains = [host, parts.length > 2 ? `.${parts.slice(-2).join(".")}` : `.${host}`];
  for (const entry of document.cookie.split("; ")) {
    const name = entry.split("=")[0];
    // PostHog's own opt-out key is `__ph_opt_in_out_<key>`, which does not
    // match this prefix, so the rejection survives.
    if (!name.startsWith(COOKIE_PREFIX)) continue;
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; Path=/; Domain=${domain}`;
    }
    document.cookie = `${name}=; Max-Age=0; Path=/`;
  }
}

// Every `niftipay.ph.*` key, including the first touch, so attribution
// data does not survive a rejection.
function clearNamespacedStorage(): void {
  const storage = window.localStorage;
  const keys: string[] = [];
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index);
    if (key !== null && key.startsWith(STORAGE_PREFIX)) keys.push(key);
  }
  for (const key of keys) storage.removeItem(key);
}

/**
 * The rejection order — `opt_out_capturing()` FIRST, then stop recording,
 * then `reset(true)`, then switch persistence to memory — then the browser
 * state. Opting out first stops new capture before anything else runs (with
 * opt-out last, a real browser sent one extra event batch and one extra
 * replay flush right after the rejection, measured 2026-09-17). It does not
 * empty the SDK's queue, though: a batch captured just before the rejection
 * may still leave within its 3 s flush interval.
 * Switching `persistence` to `"memory"` before the cookie/storage cleanup
 * stops the SDK re-creating what that cleanup then deletes: otherwise the
 * SDK re-writes the `ph_<key>_posthog` cookie after our deletion and it
 * survives until the next page load. `set_config` never touches PostHog's
 * own opt-out key (`__ph_opt_in_out_*`) — that key is written by
 * `opt_out_capturing()` above and the cleanup below must keep ignoring it.
 * The cookie and storage cleanup also runs when the SDK never started on
 * this surface, so a rejection on `/buy` (where replay and init may never
 * have happened) still clears everything.
 */
export function stopPostHog(): void {
  if (!isBrowser()) return;
  if (hasKey() && isLoaded()) {
    run("stop", () => {
      posthog.opt_out_capturing();
      posthog.stopSessionRecording();
      // `reset(true)` mints a new anonymous id and the SDK would normally
      // reload feature flags for it — one more request to the ingestion host
      // seconds after a rejection. `advanced_disable_flags: true` is set at
      // init (`init-options.ts`) and posthog-js reads that init config
      // first, so `reset(true)` already stays silent without setting it here.
      posthog.reset(true);
      // `reset(true)` also wipes `__ph_opt_in_out_*`, so opting out again is
      // what actually leaves the refusal recorded in the browser. It is
      // idempotent, and that key must survive a rejection.
      posthog.opt_out_capturing();
      posthog.set_config({ persistence: "memory" });
    });
  }
  run("stop:cookies", clearPosthogCookies);
  run("stop:storage", clearNamespacedStorage);
}

/**
 * Capture resumes after a rejection. `stopPostHog` switches `persistence`
 * to `"memory"` so the SDK cannot rewrite what the cleanup deleted; a
 * visitor who then accepts again must get working capture with normal
 * persistence, not memory-only, so it is restored here before opting back
 * in. Session recording is deliberately not restarted here — whether this
 * surface may be recorded is decided per path by the consent gate
 * (`replay-policy.ts`), which owns the pathname.
 */
export function resumePostHog(): void {
  if (!isBrowser() || !hasKey() || !isLoaded()) return;
  run("resume", () => {
    // Only persistence is put back, so a visitor who rejects and then accepts
    // again gets normal storage instead of memory-only. `advanced_disable_flags`
    // stays `true`: the `/flags/` request is never turned back on, not on
    // resume, not per surface.
    posthog.set_config({ persistence: "localStorage+cookie" });
    posthog.opt_in_capturing();
  });
}

/**
 * Applies the per-surface capture config for `pathname`
 * and starts or stops session recording to match `replay-policy.ts`. Called
 * on initial load and on every client navigation (the consent gate's
 * `useLayoutEffect`), so a blocked path stops recording before rrweb sees
 * the new page, and leaving it restores the previous surface. Recording
 * never starts while the visitor has opted out.
 */
export function applyReplayPolicy(pathname: string): void {
  if (!isBrowser() || !hasKey() || !isLoaded()) return;
  run("replay", () => {
    posthog.set_config(surfaceConfig(pathname));
    if (replayAllowed(pathname) && !posthog.has_opted_out_capturing()) {
      posthog.startSessionRecording();
    } else {
      posthog.stopSessionRecording();
    }
  });
}

/**
 * Only the event's allowlisted properties leave the browser.
 * `{ transport: "sendBeacon" }` before a hard navigation is passed
 * straight to the SDK; there is no retry queue, so an offline beacon is
 * dropped instead of replaying URLs later.
 */
export function capture(
  event: EventName,
  props?: Record<string, unknown>,
  options?: CaptureOptions,
): void {
  if (!isActive()) return;
  run("capture", () => {
    posthog.capture(event, pickAllowed(event, props), options);
  });
}

/** The caller (the pageview component) passes the redacted URL. */
export function capturePageview(url: string): void {
  if (!isActive()) return;
  run("pageview", () => {
    posthog.capture("$pageview", { $current_url: url });
  });
}

/** While opted out no `$set` leaves the browser. */
export function identifyUser(
  id: string,
  set: Record<string, unknown>,
  setOnce?: Record<string, unknown>,
): void {
  if (!isActive()) return;
  run("identify", () => {
    posthog.identify(id, set, setOnce);
  });
}

/** On sign-out; a rejection must survive the reset, so an opted-out
 *  user is left alone (`isActive` is false). */
export function resetUser(): void {
  if (!isActive()) return;
  run("reset", () => {
    posthog.reset();
  });
}
